import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Card,
  Chip,
  CircularProgress,
  Grid,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import Cookies from "js-cookie";
import CommonTable from "../../common/CommonTable";
import MauvelousPrimaryButton from "../../common/button/mauvelousPrimaryButton";
import BluePrimaryButton from "../../common/button/bluePrimaryButton";
import { FORM_ASYNC_API_URL } from "../../utils/constants";
import { cssProperties } from "../../utils/commonCssProperties";
import masterActions from "../../redux/master/actions";
import commonActions from "../../redux/common/actions";

/**
 * Document Upload Verification.
 *
 * Reports on the daily job in forms-async-api that checks every candidate upload
 * against Google Cloud Storage. The ONLY thing verified is that the object
 * exists and is larger than zero bytes - this screen is NOT about whether a
 * document is acceptable. Approving document CONTENT is the existing Data
 * Verifier / CAP Verifier workflow, which is unrelated; a file can be perfectly
 * valid to a human reviewer and still be a 0-byte object here.
 *
 * Nothing on this screen can reach a candidate's file: the API returns counts,
 * statuses and identifiers only, never a bucket, object path or signed URL.
 */

/** Matches the statuses written by the verification worker. */
const STATUS_META = {
  VERIFIED: { label: "Verified", color: "#1a7f37" },
  GCP_MISSING: { label: "GCP Missing", color: "#b42318" },
  EMPTY_FILE: { label: "Empty Files", color: "#b54708" },
  ERROR: { label: "Errors", color: "#6941c6" },
  PENDING: { label: "Pending", color: "#475467" },
};

const FAILURE_STATUSES = ["GCP_MISSING", "EMPTY_FILE", "ERROR", "PENDING"];

const formatNumber = (value) =>
  value === null || value === undefined ? "-" : Number(value).toLocaleString("en-IN");

const formatTimestamp = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const StatCard = ({ label, value, color, hint }) => (
  <Card sx={{ p: 2.5, height: "100%", borderLeft: `6px solid ${color}`, boxShadow: "0 1px 4px rgba(0,0,0,0.08)" }}>
    <Typography sx={{ fontSize: 13, color: "#667085", mb: 0.5 }}>{label}</Typography>
    <Typography sx={{ fontSize: 30, fontWeight: 700, color, lineHeight: 1.15 }}>
      {formatNumber(value)}
    </Typography>
    {hint ? <Typography sx={{ fontSize: 11, color: "#98a2b3", mt: 0.5 }}>{hint}</Typography> : null}
  </Card>
);

const failureColumns = [
  { name: "Upload ID", dataIndex: "corcandidateuploadid", render: (v) => v || "-" },
  { name: "Candidate ID", dataIndex: "candidateid", render: (v) => v || "-" },
  { name: "Course", dataIndex: "courseid", render: (v) => v || "-" },
  { name: "Doc Type", dataIndex: "documenttype", render: (v) => (v === null || v === undefined ? "-" : v) },
  {
    name: "Status",
    dataIndex: "status",
    render: (value) => (
      <Chip
        size="small"
        label={STATUS_META[value]?.label || value}
        sx={{ backgroundColor: `${STATUS_META[value]?.color || "#475467"}18`, color: STATUS_META[value]?.color || "#475467", fontWeight: 600 }}
      />
    ),
  },
  {
    name: "Size",
    dataIndex: "filesizebytes",
    render: (value) => (value === null || value === undefined ? "-" : `${formatNumber(value)} b`),
  },
  { name: "Attempts", dataIndex: "attempts", render: (v) => (v === null || v === undefined ? "-" : v) },
  {
    name: "Reason",
    dataIndex: "errormessage",
    // Reasons can be long GCS messages; keep the cell readable and put the whole
    // string in the tooltip.
    render: (value) =>
      value ? <span title={value}>{value.length > 44 ? `${value.slice(0, 44)}...` : value}</span> : "-",
  },
  { name: "Next Retry", dataIndex: "nextretryat", render: (value) => (value ? formatTimestamp(value) : "-") },
];

export default function DocumentVerification() {
  const dispatch = useDispatch();
  const { courseList } = useSelector((state) => state.masterReducer);

  const [courseId, setCourseId] = useState(0);
  const [date, setDate] = useState("");
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);

  const [failureStatus, setFailureStatus] = useState("GCP_MISSING");
  const [failures, setFailures] = useState([]);
  const [nextAfterId, setNextAfterId] = useState(null);
  const [loadingFailures, setLoadingFailures] = useState(false);
  const [triggering, setTriggering] = useState(false);

  const authHeaders = () => ({
    headers: { Authorization: `Bearer ${Cookies.get("mhet_mst_token")}` },
  });

  const alert = useCallback(
    (status, message) =>
      dispatch({ type: commonActions.SET_ALERT, payload: { show: true, status, message } }),
    [dispatch]
  );

  useEffect(() => {
    dispatch({ type: masterActions.GET_COURSE_LIST });
  }, [dispatch]);

  const buildFilterQuery = useCallback(() => {
    const params = [];
    if (date) params.push(`date=${encodeURIComponent(date)}`);
    if (courseId) params.push(`courseid=${courseId}`);
    return params.length ? `?${params.join("&")}` : "";
  }, [date, courseId]);

  const loadSummary = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(
        `${FORM_ASYNC_API_URL}/upload-verification/summary${buildFilterQuery()}`,
        authHeaders()
      );
      setSummary(data?.result || null);
    } catch (error) {
      setSummary(null);
      alert("failed", error?.response?.data?.message || "Failed to load verification summary");
    } finally {
      setLoading(false);
    }
  }, [buildFilterQuery, alert]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  /**
   * Failures are keyset-paginated: each page asks for rows after the last id it
   * saw. `reset` distinguishes a fresh query from "load more" - without it,
   * changing the status filter would append to the previous status's rows.
   */
  const loadFailures = useCallback(
    async (reset) => {
      setLoadingFailures(true);
      try {
        const params = [`status=${failureStatus}`, "limit=100"];
        if (!reset && nextAfterId) params.push(`afterId=${nextAfterId}`);
        if (courseId) params.push(`courseid=${courseId}`);

        const { data } = await axios.get(
          `${FORM_ASYNC_API_URL}/upload-verification/failures?${params.join("&")}`,
          authHeaders()
        );
        const rows = data?.result || [];
        setFailures((prev) => (reset ? rows : [...prev, ...rows]));
        setNextAfterId(data?.nextAfterId || null);
      } catch (error) {
        alert("failed", error?.response?.data?.message || "Failed to load failure list");
      } finally {
        setLoadingFailures(false);
      }
    },
    [failureStatus, nextAfterId, courseId, alert]
  );

  // A status or course change invalidates the current page entirely.
  useEffect(() => {
    setFailures([]);
    setNextAfterId(null);
  }, [failureStatus, courseId]);

  const handleRunNow = async () => {
    setTriggering(true);
    try {
      // The course selector above drives the run, not just the display.
      // courseId 0 is the "All courses" option and is sent as null, which the
      // worker reads as "no course filter".
      const { data } = await axios.post(
        `${FORM_ASYNC_API_URL}/upload-verification/run`,
        { courseid: courseId || null },
        authHeaders()
      );
      alert(data?.started ? "success" : "failed", data?.message || "Verification run requested");
      // The run is asynchronous, so the counts will not have moved yet - refresh
      // so the "running now" indicator flips immediately.
      loadSummary();
    } catch (error) {
      alert("failed", error?.response?.data?.message || "Failed to start verification run");
    } finally {
      setTriggering(false);
    }
  };

  const totals = summary?.totals;
  const lastRun = summary?.lastRun;
  const backlog = summary?.backlog;

  const verifiedPct =
    totals && totals.total ? Math.round((totals.verified / totals.total) * 100) : null;

  return (
    <Box sx={{ p: 2 }}>
      {/* Filters */}
      <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Typography sx={{ mb: 1, fontSize: 14, color: cssProperties?.color?.primary }}>
            Course
          </Typography>
          <Select fullWidth size="small" value={courseId} onChange={(e) => setCourseId(e.target.value)}>
            <MenuItem value={0}>All courses</MenuItem>
            {courseList?.all?.map((course, i) => (
              <MenuItem key={i} value={course.courseid} sx={{ whiteSpace: "nowrap" }}>
                {course.coursename}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        <Grid item xs={12} md={3}>
          <Typography sx={{ mb: 1, fontSize: 14, color: cssProperties?.color?.primary }}>
            Date
          </Typography>
          <TextField
            fullWidth
            size="small"
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
          />
        </Grid>

        <Grid item xs={12} md={2}>
          <BluePrimaryButton title="Refresh" handleSubmit={loadSummary} />
        </Grid>

        <Grid item xs={12} md={3}>
          <MauvelousPrimaryButton
            title={
              triggering
                ? "loader"
                : courseId
                  ? "Run For Selected Course"
                  : "Run For All Courses"
            }
            handleClick={handleRunNow}
            disabled={triggering || summary?.workerRunningNow}
          />
        </Grid>
      </Grid>

      {/* The date filter needs explaining: the source table has no upload
          timestamp, so a date here is the day the file was DISCOVERED by the
          job, which is the only date this data can honestly report. */}
      <Typography sx={{ fontSize: 12, color: "#98a2b3", mb: 2 }}>
        Date filters on when the file was picked up by the verification job, not when the candidate
        uploaded it.
      </Typography>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {!loading && totals && (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard label="Total Files" value={totals.total} color="#0f4f96" />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard
                label="Verified"
                value={totals.verified}
                color={STATUS_META.VERIFIED.color}
                hint={verifiedPct === null ? null : `${verifiedPct}% of total`}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard label="GCP Missing" value={totals.gcpMissing} color={STATUS_META.GCP_MISSING.color} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard label="Empty Files" value={totals.emptyFile} color={STATUS_META.EMPTY_FILE.color} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard label="Errors" value={totals.errors} color={STATUS_META.ERROR.color} />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={2}>
              <StatCard
                label="Pending"
                value={totals.pending}
                color={STATUS_META.PENDING.color}
                hint="Not checked yet"
              />
            </Grid>
          </Grid>

          {/* Job health. Without this the counts are ambiguous: a low Pending
              figure means nothing if the job last ran a week ago. */}
          <Card sx={{ mt: 3, p: 2, background: "#f7f9fc", boxShadow: "none" }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography sx={{ fontSize: 12, color: "#667085" }}>Last Run</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                  {lastRun ? formatTimestamp(lastRun.startedat) : "Never run"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography sx={{ fontSize: 12, color: "#667085" }}>Run Status</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                  {summary?.workerRunningNow ? "Running now" : lastRun?.status || "-"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography sx={{ fontSize: 12, color: "#667085" }}>Checked Last Run</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                  {formatNumber(lastRun?.checked)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={2}>
                <Typography sx={{ fontSize: 12, color: "#667085" }}>Awaiting Check</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                  {formatNumber(backlog?.pending)}
                </Typography>
              </Grid>
              <Grid item xs={12} md={3}>
                <Typography sx={{ fontSize: 12, color: "#667085" }}>Scheduled For Retry</Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 14 }}>
                  {formatNumber(backlog?.scheduled)}
                  {backlog?.dueForRetry ? ` (${formatNumber(backlog.dueForRetry)} due)` : ""}
                </Typography>
              </Grid>
            </Grid>
            {lastRun?.errormessage ? (
              <Typography sx={{ mt: 1, fontSize: 12, color: "#b42318" }}>
                Last run error: {lastRun.errormessage}
              </Typography>
            ) : null}
          </Card>
        </>
      )}

      {!loading && !totals && (
        <Typography sx={{ color: "#667085", py: 4 }}>
          No verification data yet. The job has not recorded any files for these filters.
        </Typography>
      )}

      {/* Drill-down */}
      <Box sx={{ mt: 4 }}>
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={4}>
            <Typography sx={{ mb: 1, fontSize: 14, color: cssProperties?.color?.primary }}>
              Show files with status
            </Typography>
            <Select
              fullWidth
              size="small"
              value={failureStatus}
              onChange={(e) => setFailureStatus(e.target.value)}
            >
              {FAILURE_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  {STATUS_META[status]?.label || status}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} md={3}>
            <BluePrimaryButton title="Load List" handleSubmit={() => loadFailures(true)} />
          </Grid>
        </Grid>

        {loadingFailures && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        )}

        {failures.length > 0 && (
          <>
            <CommonTable tableData={failures} columnData={failureColumns} />
            <Box sx={{ display: "flex", justifyContent: "center", gap: 2, mt: 2 }}>
              <Typography sx={{ fontSize: 13, color: "#667085", alignSelf: "center" }}>
                Showing {formatNumber(failures.length)} row(s)
              </Typography>
              {nextAfterId ? (
                <BluePrimaryButton title="Load More" handleSubmit={() => loadFailures(false)} />
              ) : null}
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
}
